import { useState } from 'react'

import { TextInput } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'

import { useQuery, useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  DELETE_DOCUMENT_TEMPLATE_PACKAGE,
  GET_DOCUMENT_TEMPLATE_PACKAGES,
} from 'src/graphql/documentTemplatePackages.graphql'
import usePage from 'src/hooks/usePage/usePage'
import usePageSize from 'src/hooks/usePageSize/usePageSize'
import IconSearch from 'src/icons/IconSearch'

import DocumentPackageDrawer from './components/DocumentPackageDrawer'
import DocumentPackagesTable from './components/DocumentPackagesTable'

interface DocumentPackage {
  documentTemplatePackageId: string
  packageName: string
  createdAt: string
  documentTemplates: Array<{
    documentTemplateId: string
    documentName: string
    documentCategory: string
  }>
}

const DocumentPackagesTab = () => {
  const [page, setPage] = usePage()
  const [pageSize, setPageSize] = usePageSize(20)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300)

  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null
  )
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('edit')
  const [packageToDelete, setPackageToDelete] = useState<DocumentPackage | null>(
    null
  )

  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false)
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false)

  const { data, loading } = useQuery(GET_DOCUMENT_TEMPLATE_PACKAGES, {
    variables: { searchTerm: debouncedSearchTerm || undefined },
  })

  const [deletePackage] = useMutation(DELETE_DOCUMENT_TEMPLATE_PACKAGE, {
    refetchQueries: [GET_DOCUMENT_TEMPLATE_PACKAGES],
    onCompleted: () => {
      toast('Package deleted successfully', 'success')
    },
    onError: () => {
      toast('Unable to delete package, please try again.', 'error')
    },
  })

  const handleAddPackage = () => {
    setSelectedPackageId(null)
    setDrawerMode('create')
    openDrawer()
  }

  const handleRowClick = (packageId: string) => {
    setSelectedPackageId(packageId)
    setDrawerMode('edit')
    openDrawer()
  }

  const handleDeleteClick = (pkg: DocumentPackage) => {
    setPackageToDelete(pkg)
    openDeleteModal()
  }

  const handleConfirmDelete = async () => {
    if (packageToDelete) {
      await deletePackage({
        variables: {
          documentTemplatePackageId: packageToDelete.documentTemplatePackageId,
        },
      })
      setPackageToDelete(null)
    }
  }

  const handleCloseDrawer = () => {
    setSelectedPackageId(null)
    setDrawerMode('edit')
    closeDrawer()
  }

  const packages = data?.documentTemplatePackages ?? []

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-shrink-0 flex-row justify-between">
        <div className="flex flex-col">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            E-Signature Packages
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            {packages.length} e-signature package{packages.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <TextInput
            placeholder="Search packages..."
            leftSection={
              <IconSearch className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            className="w-64"
          />
          <DNButton
            variant="primary"
            onClick={handleAddPackage}
          >
            Add Package
          </DNButton>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <DocumentPackagesTable
          packages={packages}
          loading={loading}
          onRowClick={handleRowClick}
          onDeleteClick={handleDeleteClick}
          defaultResultsPerPage={pageSize}
          totalCount={packages.length}
          onPageChange={setPage}
          paginationProps={{
            initialPage: page,
            onResultsPerPageChange: setPageSize,
          }}
        />
      </div>

      <DocumentPackageDrawer
        opened={drawerOpened}
        onClose={handleCloseDrawer}
        packageId={selectedPackageId}
        mode={drawerMode}
      />

      <DeleteModal
        title="Delete Package"
        description={
          packageToDelete ? (
            <div className="space-y-2">
              <p>Are you sure you want to delete this package?</p>
              <div className="rounded-md bg-doubleNickel-gray-50 p-3">
                <p className="font-medium text-doubleNickel-gray-700">
                  {packageToDelete.packageName}
                </p>
                {packageToDelete.documentTemplates?.length > 0 && (
                  <p className="mt-1 text-xs text-doubleNickel-gray-500">
                    Contains {packageToDelete.documentTemplates.length} document
                    {packageToDelete.documentTemplates.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          ) : (
            'This action cannot be undone.'
          )
        }
        opened={deleteModalOpened}
        close={closeDeleteModal}
        onDelete={handleConfirmDelete}
      />
    </div>
  )
}

export default DocumentPackagesTab
